import { html, LitElement, nothing, PropertyValues } from "lit";
import { customElement, property } from "lit/decorators";
import { VesselPlanningModel, VoyagePlan } from "../../../src/models/vessel-planning-model";
import { buildRoutingOptions, convertLocalToUTC, EtaComparison, EtaMessageTypes, formatEtaDate, formatUTCDateTimeString, getEtaComparisonData, getMetricsData, getParsedData, getPlanWithNewEta, postEtaChangeRequest, RouteMetricsComparison, showEtaRequestClf, updateArrivalPilotEtaToRouteGeometry } from "./helper";
import { EtaChangeReviewWrapperStyles } from "./eta-change-review-styles";
import { VoyageDialogPopupManager } from "../../voyage-dialog-popup/src/voyage-dialog-popup-manager";
import { VesselLoaderManager } from "../../vessel-loader/src/vessel-loader-manager";
import { areArrivalEtasValid, markPassedAndAddCurrentPosition, verifyRequestedETAs, verifyWarningsFromCalculation } from "../../../src/utils/route-utils";
import { VoyageRoutingModel } from "../../../src/models/voyage-routing-model";
import { VoyageToastManager } from "../../voy-toast/src/voy-toast-manager";
import { AlertType, VesselAlertsModel } from "../../../src/models/vessel-alerts-model";
import { ScheduleChangeModel } from "../../../src/models/schedule-change-model";
import { VoyageImoModel } from "../../../src/models/voyage-imo-model";

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;
const getCurrentVesselImo = () => isVesselOnCloud ? VoyageImoModel!.imo! : (<any>window)["appenv"].vesselIMONumber;

@customElement('eta-change-requested-wrapper')
export class EtaChangeRequestedWrapper extends LitElement {
    @property({ type: Object }) _currentExecutingPlan: VoyagePlan | null = null
    @property({ type: Object }) _planWithUpdatedEta: VoyagePlan | null = null
    @property({ type: Boolean }) isLoading = true;
    @property({ type: Boolean }) hasRequestedEta = false;
    @property({ type: Boolean }) canContinue = false;
    private newEta: string = '';
    private metrics : RouteMetricsComparison[] = [];
    private etaData : EtaComparison[] = [];
    private arrivalTerminalCode: string = '';
    private newArrivalBerthTime : string | undefined = '';
    private newArrivalPilotTime : string | undefined = '';
    static styles = [EtaChangeReviewWrapperStyles]
    private requestedTime: string = '';

    constructor(){
        super()
        this.getPlans();
        this.getPlans = this.getPlans.bind(this);
        this.loadCardDetails = this.loadCardDetails.bind(this);
        this.setLoading = this.setLoading.bind(this);
        this.optimisePlans = this.optimisePlans.bind(this);
        this.fetchRequestedAlert = this.fetchRequestedAlert.bind(this)
    }

    private getLoaderHeaderText() {
        return this.newEta
            ? `Request ETA Change ${this.newEta} LT`
            : 'Request ETA Change';
    }

    updated(changedProps: PropertyValues) {
        super.updated(changedProps);

        if (changedProps.has('isLoading')) {
            const eventName = this.isLoading ? 'hide-panel' : 'show-panel';

            this.dispatchEvent(new CustomEvent(eventName, {
            bubbles: true,
            composed: true
            }));
        }
    }

    private async fetchRequestedAlert() {
        const alert = await ScheduleChangeModel!.getEtaRequestAlert();
        this._currentExecutingPlan = await VesselPlanningModel!.getExecutingPlan();
        this.arrivalTerminalCode = this._currentExecutingPlan?.arrivalTerminalCode ?? '';
            if (!this._currentExecutingPlan) {
                console.info('No executing plan found to show ETA change review');
                VoyageDialogPopupManager.createDialog({
                    headerText: 'Failed to find an executing plan',
                    isError: true,
                    bodyText: 'Please check if the current voyage plan is approved and Try again.'
                });
                this.setLoading(false);
                this.handleClose();
                return;
            }
        if (alert) {
            const data = getParsedData(alert.data);
            if(data.schedule_id !== this._currentExecutingPlan.scheduleId){
                // Alert does not belong to current active schedule
                return;
            }

            this.hasRequestedEta = true;
            this.requestedTime = formatUTCDateTimeString(data.deviation_remark_timestamp);
            this.newArrivalBerthTime = data.earliest_requested_eta.arrivalTimings.array[0].timestamp;
            this.newArrivalPilotTime = data.earliest_requested_eta.arrivalTimings.array[1].timestamp;

            const planBerthTime = this._currentExecutingPlan?.eta ? new Date(this._currentExecutingPlan.eta).getTime() : undefined;
            const newArrivalBerthTimeMs = this.newArrivalBerthTime ? new Date(this.newArrivalBerthTime).getTime() : undefined;

            const onlyPilotTimeIsChanged = planBerthTime && newArrivalBerthTimeMs && newArrivalBerthTimeMs === planBerthTime;

            this.newEta = onlyPilotTimeIsChanged ? await formatEtaDate(this.newArrivalPilotTime, this.arrivalTerminalCode) : await formatEtaDate(this.newArrivalBerthTime, this.arrivalTerminalCode);

            this._planWithUpdatedEta = getPlanWithNewEta(this._currentExecutingPlan, this.newArrivalBerthTime, this.newArrivalPilotTime);
            await this.reOptimisePlans();
        }
    }
    
    private async getPlans(){
        try{
            this.setLoading(true);

            //loader is introduced here to handle the delay for calculating the chart and card details
            VesselLoaderManager.createLoader({
                headerText: this.getLoaderHeaderText(),
                showCancelButton: true,
            });

            await this.fetchRequestedAlert();
            await this.loadCardDetails(); 

        }catch(e){
            VoyageDialogPopupManager.createDialog({
                headerText: 'Failed to optimize the plans',
                isError: true,
                bodyText: 'Please update the vessel position and route geometry. Try again.'
            });
        } finally {
            VesselLoaderManager.removeLoader();
            this.setLoading(false);
        } 
    }

    private async loadCardDetails(){
        if (!this._currentExecutingPlan) return;
        const [metrics, etaData] = await Promise.all([
            getMetricsData(this._currentExecutingPlan, this._planWithUpdatedEta),
            getEtaComparisonData(this._currentExecutingPlan, this._planWithUpdatedEta, this.arrivalTerminalCode)
        ]);

        this.metrics = metrics;
        this.etaData = etaData;
    }

    private async optimisePlans(evt: CustomEvent){
        if(!this._currentExecutingPlan)
            return;
        const newArrivalPilotTimeLt = evt.detail.pilotEta ?? this._currentExecutingPlan.arrivalPilotEta;
        const newArrivalBerthTimeLt = evt.detail.berthEta ?? this._currentExecutingPlan.eta;

        this.arrivalTerminalCode = this._currentExecutingPlan?.arrivalTerminalCode;
        
        this.newArrivalBerthTime = await convertLocalToUTC(newArrivalBerthTimeLt, this.arrivalTerminalCode, this._currentExecutingPlan.eta ? new Date(this._currentExecutingPlan.eta).toISOString() : undefined);
        this.newArrivalPilotTime = await convertLocalToUTC(newArrivalPilotTimeLt, this.arrivalTerminalCode, this._currentExecutingPlan.arrivalPilotEta ? new Date(this._currentExecutingPlan.arrivalPilotEta).toISOString() : undefined);

        const arrivalPilotTimeMs = this.newArrivalPilotTime ? new Date(this.newArrivalPilotTime).getTime() : NaN;
        const arrivalBerthTimeMs = this.newArrivalBerthTime ? new Date(this.newArrivalBerthTime).getTime() : NaN;
        if (!areArrivalEtasValid(this._currentExecutingPlan, arrivalPilotTimeMs, arrivalBerthTimeMs)) return;

        const planBerthTime = this._currentExecutingPlan?.eta ? new Date(this._currentExecutingPlan.eta).getTime() : undefined;
        const newArrivalBerthTimeMs = this.newArrivalBerthTime ? new Date(this.newArrivalBerthTime).getTime() : undefined;

        const onlyPilotTimeIsChanged = planBerthTime && newArrivalBerthTimeMs && newArrivalBerthTimeMs === planBerthTime;
        const newEtaRequested = onlyPilotTimeIsChanged ? (this.newArrivalPilotTime ?? "") : (this.newArrivalBerthTime ?? "");
        this.newEta = await formatEtaDate(newEtaRequested, this.arrivalTerminalCode);

        this._planWithUpdatedEta = getPlanWithNewEta(this._currentExecutingPlan, this.newArrivalBerthTime, this.newArrivalPilotTime);
        VesselLoaderManager.createLoader({
            headerText: this.getLoaderHeaderText(),
            showCancelButton: true,
        });

        let success = false;

        try {
            await this.reOptimisePlans();
            await this.loadCardDetails();
            success = true;
        } catch (err) {
            success = false;
        } finally {
            VesselLoaderManager.removeLoader();

            await verifyRequestedETAs(this._planWithUpdatedEta?.routes[0], this._planWithUpdatedEta, this._planWithUpdatedEta.scheduleId, this._planWithUpdatedEta.selectedRouteIndex);
            await verifyWarningsFromCalculation(this._planWithUpdatedEta?.routes[0], this._planWithUpdatedEta, this._planWithUpdatedEta.scheduleId, this._planWithUpdatedEta.selectedRouteIndex);

            this.canContinue = success;
            VoyageToastManager.createToast({
                message: success ? EtaMessageTypes.Success : EtaMessageTypes.Failure,
                type: success ? "success" : "error",
            });
        }
    }

    private async reOptimisePlans() {
        if(!this._currentExecutingPlan || !this._planWithUpdatedEta)
            return;

        const currentRoute = this._currentExecutingPlan?.routes[this._currentExecutingPlan?.selectedRouteIndex || 0];
        const newRoute = this._planWithUpdatedEta?.routes[this._planWithUpdatedEta?.selectedRouteIndex || 0];

        const currentOptions = buildRoutingOptions(this._currentExecutingPlan!, currentRoute, true, false);
        const newOptions = {
            ...currentOptions,
            updatePlan: undefined,
            eta: this.newArrivalBerthTime ? new Date(this.newArrivalBerthTime).getTime() : this._planWithUpdatedEta?.eta,
        }
        const isPastETD = (this._currentExecutingPlan?.etd ?? 0) < Date.now();
        
        const updatedCurrentRoute = isPastETD ? await markPassedAndAddCurrentPosition(currentRoute) : currentRoute;
        const updatedNewRoute = updateArrivalPilotEtaToRouteGeometry(this.newArrivalPilotTime!, newRoute);
        
        const [_, recalculatedNewRoute] = await Promise.all([
            VoyageRoutingModel!.postRoutingJobUsingRoute(updatedCurrentRoute, currentOptions, true),
            VoyageRoutingModel!.postRoutingJobUsingRoute(updatedNewRoute, newOptions, true),
        ]);
        
        this._currentExecutingPlan = await VesselPlanningModel!.getExecutingPlan();
        this._planWithUpdatedEta!.update_dtm = new Date().toISOString();
        this._planWithUpdatedEta = { ...this._planWithUpdatedEta!, routes: [recalculatedNewRoute]};
    }

    private setLoading(value: boolean) {
        this.isLoading = value;
    }

    private async handleContinue(){
        const etaBerth = await formatEtaDate(this.newArrivalBerthTime, this.arrivalTerminalCode);
        const etaPilot = await formatEtaDate(this.newArrivalPilotTime, this.arrivalTerminalCode);
        const requestPayload = await showEtaRequestClf(this.newEta, AlertType.SuccessAlert, etaBerth, etaPilot)
        if(requestPayload){
            this.handleClose();
            VesselAlertsModel!.addAlert(getCurrentVesselImo(), {
                title: 'Requested new ETA',
                message: 'We informed TBMs of our reason for the new ETA change and earliest ETA. Please await their response.',
                id: `${this.newEta}`,
                type: AlertType.SuccessAlert,
            });
            await postEtaChangeRequest(this._planWithUpdatedEta, requestPayload);
        }
    }

    private handleClose() {
        this.dispatchEvent(new Event('back-clicked'))
    }

    private renderActionSection(){
        return html`
            <div class="action-section">
                <div class="instructions">
                    You can proceed after calculating your new earliest ETA value above.
                </div>
                <div class="actions">
                    <mc-button
                        appearance="primary"
                        icon="check"
                        width="full-width"
                        @click=${this.handleContinue}
                        ?disabled=${!this.canContinue}
                        >Continue</mc-button
                    >
                    <mc-button
                        appearance="neutral"
                        width="full-width"
                        @click=${this.handleClose}
                        >Close</mc-button
                    >
                </div>
            </div>
        `
    }
    
    private renderHeader() {
        return html`
            <div class="heading">
                <div class="title-wrapper">
                    <span class="title">Request new ETA ${this.newEta} LT ${this.hasRequestedEta ? html`<span class="requested">Requested on ${formatUTCDateTimeString(this.requestedTime)}</span>` : nothing}</span>
                    <p class="description">
                        Please enter a new ETA value in the Earliest ETA field.
                    </p>
                </div>
                <div class="heading-status-contents">
                            <mc-tooltip position="right-top">
                                <button
                                    class="back-button"
                                    slot="trigger"
                                    @click=${this.handleClose}
                                >
                                    <mc-icon icon="times"></mc-icon>
                                </button>
                                <span>Close ETA Change Request</span>
                            </mc-tooltip>
                </div>
            </div>
        `
    }

    private renderCharts() {
        return html`
            <div class="charts-block">
                ${this.renderChartCard()}
            </div>
        `
    }

    private renderChartCard() {
        return html`
            <div class="chart-card">
                <div class="chart-content">
                    <eta-change-chart .requestedPlan=${this._planWithUpdatedEta}></eta-change-chart>
                </div>
            </div>
        `
    }    

    private renderDetailsCard() {
        if (!this.metrics || !this.etaData) return html``;
        return html`
            <div class="details-card">
                <eta-details-table .metrics=${this.metrics}></eta-details-table>
                <div class="eta-actions-section">
                <eta-comparison-table .etaData=${this.etaData} .etaIsRequested=${true} .plan=${this._currentExecutingPlan} @eta-requested=${(e: CustomEvent) => this.optimisePlans(e)} .planEtd=${this._currentExecutingPlan?.etd}></eta-comparison-table>
                    ${this.renderActionSection()}
                </div>
            </div>
        `
    }

    render(){
        if (this.isLoading) {
        return html``;
        }
        return html`
                    <div class="eta-change-review">
                        ${this.renderHeader()} 
                        <div class="eta-change-review-content">
                            ${this.renderCharts()}
                            ${this.renderDetailsCard()}
                        </div>
                    </div>
        `
    }
}